import React, { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Alert, Modal, Popconfirm, Card, Select, Input, Button, Space, Typography, Radio, message, Tabs, Tag, Tooltip } from 'antd';
import { 
  DeleteOutlined, 
  InfoCircleOutlined, 
  UserOutlined, 
  SettingOutlined,
  CheckCircleOutlined,
  EditOutlined,
  TeamOutlined,
  SyncOutlined
} from '@ant-design/icons';
import axios from 'axios';
import NodeInputHandler from '../common/NodeInputHandler';
import GenericNodeContent from '../common/GenericNodeContent';

const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;

const HumanInputNode = ({ id, data, isConnectable }) => {
  const [inputType, setInputType] = useState(data.inputType || 'approval');
  const [assignmentType, setAssignmentType] = useState(data.assignmentType || 'user');
  const [selectedUser, setSelectedUser] = useState(data.selectedUser || '');
  const [selectedRole, setSelectedRole] = useState(data.selectedRole || '');
  const [content, setContent] = useState(data.content || '');
  const [instructions, setInstructions] = useState(data.instructions || '');
  const [nodeName, setNodeName] = useState(data.nodeName || `Human Input ${id}`);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [activeTab, setActiveTab] = useState('input');
  const [isLoading, setIsLoading] = useState(false);
  const [permissionsModalVisible, setPermissionsModalVisible] = useState(false);

  useEffect(() => {
    fetchUsersAndRoles();
  }, []);

  useEffect(() => {
    data.updateNodeData(id, { 
      inputType, 
      assignmentType,
      selectedUser, 
      selectedRole, 
      content,
      instructions, 
      nodeName 
    });
  }, [inputType, assignmentType, selectedUser, selectedRole, content, instructions, nodeName, id, data]);

  const fetchUsersAndRoles = async () => {

    if (data.isViewer) {
      return;
    }
    
    setIsLoading(true);
    try {
      const [usersResponse, rolesResponse, taskRolesResponse] = await Promise.all([
        axios.get(`${window.wpAiWorkflowsSettings.root}wp-ai-workflows/v1/users`, {
          headers: { 'X-WP-Nonce': window.wpAiWorkflowsSettings.nonce }
        }),
        axios.get(`${window.wpAiWorkflowsSettings.root}wp-ai-workflows/v1/roles`, {
          headers: { 'X-WP-Nonce': window.wpAiWorkflowsSettings.nonce }
        }),
        axios.get(`${window.wpAiWorkflowsSettings.root}wp-ai-workflows/v1/task-roles`, {
          headers: { 'X-WP-Nonce': window.wpAiWorkflowsSettings.nonce }
        })
      ]);
      
      const taskRoles = taskRolesResponse.data;
      
      // Filter roles
      const filteredRoles = {};
      Object.entries(rolesResponse.data).forEach(([key, value]) => {
        if (taskRoles.includes(key)) {
          filteredRoles[key] = value;
        }
      });
  
      // Debug log to check data structure
      console.log('Users response:', usersResponse.data);
      console.log('Task roles:', taskRoles);
  
      // Filter users that have roles with task access
      const filteredUsers = usersResponse.data.filter(user => {
        // Check if user_roles exists in the user object
        return user.roles.some(role => taskRoles.includes(role));
      });
  
      console.log('Filtered users:', filteredUsers);
      
      setUsers(filteredUsers);
      setRoles(filteredRoles);
  
    } catch (error) {
      console.error('Error fetching users and roles:', error);
      message.error('Failed to fetch users and roles');
    } finally {
      setIsLoading(false);
    }
  };

  const getInputTypeInfo = (type) => {
    const types = {
      'approval': { icon: <CheckCircleOutlined />, label: 'Approval', tag: 'Approve' },
      'modification': { icon: <EditOutlined />, label: 'Modification', tag: 'Modify' }
    };
    return types[type] || types['approval'];
  };

  const handleContentChange = (nodeId, newData) => {
    data.updateNodeData(nodeId, newData);
  };

  const showPermissionsModal = () => {
    setPermissionsModalVisible(true);
  };
  
  // Add helper function for settings navigation
  const goToSettings = () => {
    window.location.href = `${window.wpAiWorkflowsSettings.adminUrl}admin.php?page=wp-ai-workflows&action=settings`;
  };

  return (
    <>
      <Modal
        title="Task Permissions Configuration Required"
        open={permissionsModalVisible}
        onCancel={() => setPermissionsModalVisible(false)}
        footer={[
          <Button key="settings" type="primary" onClick={goToSettings}>
            Go to Settings
          </Button>,
          <Button key="cancel" onClick={() => setPermissionsModalVisible(false)}>
            Close
          </Button>
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            type="info"
            showIcon
            message="Task Access Configuration"
            description={
              <div>
                <p>To assign tasks to users and roles, you need to first configure which roles have access to tasks in the settings.</p>
                <ul>
                  <li>Administrators always have access to tasks by default</li>
                  <li>Other roles need to be explicitly granted task access</li>
                  <li>Only users with task access can be assigned tasks</li>
                  <li>Only roles with task access will appear in the role selection</li>
                </ul>
                <p>Click "Go to Settings" to configure task access for different roles.</p>
              </div>
            }
          />
        </div>
      </Modal>

      <Card 
        title={
          <Space>
            <UserOutlined style={{ fontSize: '18px', color: '#1890ff' }}/>
            <span>Human Input</span>
            <Tag color="blue">{getInputTypeInfo(inputType).tag}</Tag>
          </Space>
        }
        style={{ width: 380 }}
        extra={
          <Space>
            <Popconfirm
              title="Delete Node"
              description="Are you sure you want to delete this node?"
              onConfirm={() => data.onDelete(id)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        }
      >
        <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
        
        <Input
          placeholder="Node Name"
          value={nodeName}
          onChange={(e) => setNodeName(e.target.value)}
          style={{ marginBottom: 16 }}
          prefix={<InfoCircleOutlined style={{ color: '#bfbfbf' }} />}
          className="nodrag"
        />

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Input" key="input">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Space align="center" style={{ marginBottom: 4 }}>
                  <Text strong>Input Type</Text>
                  <Tooltip title="Select the type of human input required">
                    <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                  </Tooltip>
                </Space>
                <Select
                  style={{ width: '100%', marginBottom: 16 }}
                  value={inputType}
                  onChange={setInputType}
                  className="nodrag"
                >
                  <Option value="approval">
                    <Space>
                      <CheckCircleOutlined />
                      <span>Approval Required</span>
                    </Space>
                  </Option>
                  <Option value="modification">
                    <Space>
                      <EditOutlined />
                      <span>Modification Required</span>
                    </Space>
                  </Option>
                </Select>
              </div>

              <div>
                <Space align="center" style={{ marginBottom: 4, width: '100%', justifyContent: 'space-between' }}>
                  <Space>
                    <Text strong>Assignment</Text>
                    <Button 
                      type="text" 
                      icon={<SyncOutlined />} 
                      onClick={fetchUsersAndRoles}
                      loading={isLoading}
                      size="small"
                    />
                  </Space>
                  <Tooltip title="Configure task permissions">
                    <Button 
                      type="link" 
                      size="small"
                      onClick={showPermissionsModal}
                      icon={<SettingOutlined />}
                    >
                      Permissions
                    </Button>
                  </Tooltip>
                </Space>

                <Radio.Group
                  onChange={(e) => setAssignmentType(e.target.value)}
                  value={assignmentType}
                  style={{ width: '100%', marginBottom: 8 }}
                  className="nodrag"
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Radio value="user">
                      <Space>
                        <UserOutlined />
                        <span>Assign to User</span>
                      </Space>
                    </Radio>
                    <Radio value="role">
                      <Space>
                        <TeamOutlined />
                        <span>Assign to Role</span>
                      </Space>
                    </Radio>
                  </Space>
                </Radio.Group>

                {assignmentType === 'user' && (
                  <>
                    <Select
                      style={{ width: '100%', marginBottom: 8 }}
                      value={selectedUser}
                      onChange={setSelectedUser}
                      placeholder="Select User"
                      className="nodrag"
                      loading={isLoading}
                      showSearch
                      optionFilterProp="children"
                      notFoundContent={users.length === 0 ? "No users with task access" : undefined}
                    >
                      {users.map(user => (
                        <Option key={user.ID} value={user.ID}>{user.display_name}</Option>
                      ))}
                    </Select>
                    {users.length === 0 && (
                      <Alert
                        type="warning"
                        message="No users available"
                        description="Users need to be granted task access in settings before they can be assigned tasks."
                        action={
                          <Button size="small" type="primary" onClick={goToSettings}>
                            Configure Access
                          </Button>
                        }
                      />
                    )}
                  </>
                )}

                {assignmentType === 'role' && (
                  <>
                    <Select
                      style={{ width: '100%', marginBottom: 8 }}
                      value={selectedRole}
                      onChange={setSelectedRole}
                      placeholder="Select Role"
                      className="nodrag"
                      loading={isLoading}
                      showSearch
                      optionFilterProp="children"
                      notFoundContent={Object.keys(roles).length === 0 ? "No roles with task access" : undefined}
                    >
                      {Object.entries(roles).map(([key, value]) => (
                        <Option key={key} value={key}>{value}</Option>
                      ))}
                    </Select>
                    {Object.keys(roles).length <= 1 && (
                      <Alert
                        type="warning"
                        message="Only Administrator available"
                        description="Additional roles need to be granted task access in settings before they can be assigned tasks."
                        action={
                          <Button size="small" type="primary" onClick={goToSettings}>
                            Configure Access
                          </Button>
                        }
                      />
                    )}
                  </>
                )}
              </div>

              <div>
                {inputType === 'modification' && (
                  <>
                    <Space align="center" style={{ marginBottom: 4 }}>
                      <Text strong>Instructions</Text>
                      <Tooltip title="Instructions for the assigned user">
                        <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                      </Tooltip>
                    </Space>
                    <GenericNodeContent
                      id={id}
                      data={{ ...data, content: instructions }}
                      onChange={(_, newData) => setInstructions(newData.content)}
                      placeholder="Enter instructions for modification..."
                    />
                  </>
                )}

                <Space align="center" style={{ marginBottom: 4, marginTop: 16 }}>
                  <Text strong>Content</Text>
                  <Tooltip title={inputType === 'approval' ? 'Content to be approved' : 'Content to be modified'}>
                    <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                  </Tooltip>
                </Space>
                <GenericNodeContent
                  id={id}
                  data={{ ...data, content }}
                  onChange={(_, newData) => setContent(newData.content)}
                  placeholder={`Enter content for ${inputType}...`}
                />
              </div>
            </Space>
          </TabPane>

          <TabPane tab="Output" key="output">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="secondary">
                Available outputs from this node:
              </Text>
              <div style={{ 
                backgroundColor: '#f5f5f5', 
                padding: '12px', 
                borderRadius: '6px'
              }}>
                {inputType === 'approval' ? (
                  <Space direction="vertical">
                    <div>
                      <Text strong>Approved Content</Text>
                      <div>
                        <Text code>[Input from {id}] (via Approve)</Text>
                      </div>
                    </div>
                    <div>
                      <Text strong>Reverted Content</Text>
                      <div>
                        <Text code>[Input from {id}] (via Revert)</Text>
                      </div>
                    </div>
                  </Space>
                ) : (
                  <div>
                    <Text strong>Modified Content</Text>
                    <div>
                      <Text code>[Input from {id}]</Text>
                    </div>
                  </div>
                )}
              </div>
            </Space>
          </TabPane>
        </Tabs>

        {/* Output Handles */}
        {inputType === 'approval' ? (
          <>
            <div style={{ position: 'absolute', right: -8, top: '30%', display: 'flex', alignItems: 'center' }}>
              <Text style={{ marginRight: 5, color: '#52c41a', writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Approve</Text>
              <Handle
                type="source"
                position={Position.Right}
                id="approve"
                style={{ top: 'auto', right: -4, background: '#52c41a', width: 12, height: 12 }}
                isConnectable={isConnectable}
              />
            </div>
            <div style={{ position: 'absolute', right: -8, top: '70%', display: 'flex', alignItems: 'center' }}>
              <Text style={{ marginRight: 5, color: '#faad14', writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Revert</Text>
              <Handle
                type="source"
                position={Position.Right}
                id="revert"
                style={{ top: 'auto', right: -4, background: '#faad14', width: 12, height: 12 }}
                isConnectable={isConnectable}
              />
            </div>
          </>
        ) : (
          <div style={{ position: 'absolute', right: -8, top: '50%', display: 'flex', alignItems: 'center' }}>
            <Text style={{ marginRight: 5, color: '#1890ff', writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Modify</Text>
            <Handle
              type="source"
              position={Position.Right}
              id="modify"
              style={{ top: 'auto', right: -4, background: '#1890ff', width: 12, height: 12 }}
              isConnectable={isConnectable}
            />
          </div>
        )}
      </Card>
    </>
  );
};

export default HumanInputNode;